import { uniqueId } from 'cadenza/utils/unique-id';
import { assert } from 'cadenza/utils/custom-error';

export const ID = 'payloadId';

/**
 * The only requirement is: The output has to look like:
 * {
 *     payloadId: <value has to be copied from the input>,
 *     output: <whatever JSON | primitive you want, it will be used to resolve the submission promise>
 * }.
 * And the top level errors thrown from the worker have to be a subtype of WebWorkerError
 */
export class WebWorkerApi {

  _idToPromiseResolve: { [key: string]: (value: unknown) => void } = {};
  _idToPromiseReject: { [key: string]: (value: unknown) => void } = {};
  _worker: Worker;

  constructor (worker: Worker) {
    this._worker = worker;

    this._worker.addEventListener('message', ({ data: { [ID]: id, ...otherData } }) => {
      assert(id != null && otherData != null && otherData.output !== undefined,
        'Invalid response format. Data should contain "payloadId" and "output"');
      const promiseResolve = this._idToPromiseResolve[id];
      delete this._idToPromiseResolve[id];
      assert(promiseResolve, 'Could not retrieve a valid promise resolve. Did you pass a correct payloadId?');
      promiseResolve(otherData.output);
    });

    this._worker.addEventListener('error', (event: ErrorEvent) => {
      const regExp = new RegExp('payloadId:\\s?(.*)\\s?\\.', 'gm');
      const payloadIdMatch = regExp.exec(event.message);
      assert(payloadIdMatch && payloadIdMatch[1], 'Please make sure the error message contains a payloadId with a value followed by a "."');

      const errorMessage = event.message.replace(regExp, '');
      const payloadId = payloadIdMatch[1];
      const promiseReject = this._idToPromiseReject[payloadId];
      delete this._idToPromiseReject[payloadId];
      assert(promiseReject, 'Could not retrieve a valid promise reject. Did you pass a correct payloadId?');
      promiseReject(new Error(`${errorMessage}. In ${event.filename} on line: ${event.lineno} col: ${event.colno}`));
    });
  }

  protected submit (input: unknown) {
    return new Promise((resolve, reject) => {
      const id = uniqueId();
      this._idToPromiseResolve[id] = resolve;
      this._idToPromiseReject[id] = reject;
      this._worker.postMessage({ input, [ID]: id });
    });
  }

}
